import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FiShield, FiActivity, FiTag, FiLock, FiKey, FiSearch, FiFileText, FiChevronLeft, FiChevronRight, FiCheck } from "react-icons/fi";
import { useAssetCreation } from "../AssetCreationContext";
import { getUserRole, UserRole } from "../../../../utils/api/endpoints/user/credentials";

interface Step {
    id: number;
    title: string;
    description: string;
    sidebarLabel: string;
    icon: React.ReactNode;
}

const WIZARD_STEPS: Step[] = [
    {
        id: 1,
        title: "Asset Identity",
        description: "Describe what product you are creating an asset for.",
        sidebarLabel: "Asset Identity",
        icon: <FiShield />,
    },
    {
        id: 2,
        title: "Lifecycle & Usage",
        description: "Understand the development stage and audience of this product.",
        sidebarLabel: "Lifecycle & Usage",
        icon: <FiActivity />,
    },
    {
        id: 3,
        title: "Asset Classification",
        description: "Classify the product for governance and business criticality.",
        sidebarLabel: "Classification",
        icon: <FiTag />,
    },
    {
        id: 4,
        title: "Data & Privacy",
        description: "Assess the sensitivity and residency of data handled by this product.",
        sidebarLabel: "Data & Privacy",
        icon: <FiLock />,
    },
    {
        id: 5,
        title: "Access Control",
        description: "Define how users authenticate and what authorization model is used.",
        sidebarLabel: "Access Control",
        icon: <FiKey />,
    },
    {
        id: 6,
        title: "Security Testing",
        description: "Specify internet exposure and allowed testing types.",
        sidebarLabel: "Security Testing",
        icon: <FiSearch />,
    },
    {
        id: 7,
        title: "Governance",
        description: "Confirm legal ownership and final approval details.",
        sidebarLabel: "Governance",
        icon: <FiFileText />,
    },
];

const WizardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentStep, goToStep, isStepValid, nextStep, prevStep, saveDraft, isSaving, submitAsset, isSubmitting, isFormValid, assetData } = useAssetCreation();
    const navigate = useNavigate();

    const userRole = getUserRole();
    const isAdminRole = userRole !== undefined && [
        UserRole.ADMIN,
        UserRole.SUB_ADMIN,
        UserRole.ADMIN_MANAGER
    ].includes(userRole);

    const canEarlyFinish = isFormValid() && !!assetData.id;

    const handleNext = async () => {
        if (currentStep === WIZARD_STEPS.length || canEarlyFinish) {
            const createdAsset = await submitAsset();
            if (createdAsset) {
                const targetCid = assetData.company_id || createdAsset.company_id;
                const path = (isAdminRole && targetCid)
                    ? `/dashboard/companies/${targetCid}/assets/${createdAsset.id}`
                    : `/dashboard/assets/${createdAsset.id}`;
                navigate(path, { state: { isNew: true } });
            }
        } else {
            nextStep();
        }
    };

    // Map currentStep to step data
    const activeStepData = WIZARD_STEPS.find(s => s.id === currentStep) || WIZARD_STEPS[0];

    // Calculate progress (excluding Intro)
    const completedCount = WIZARD_STEPS.filter(s => isStepValid(s.id)).length;
    const progressPercent = Math.round((completedCount / WIZARD_STEPS.length) * 100);

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [currentStep]);

    return (
        <div className="h-full bg-[#f8f9fa] flex flex-col lg:flex-row">
            {/* Sidebar */}
            <aside className="w-full lg:w-[310px] bg-white border-r border-gray-100 p-6 flex flex-col gap-8 sticky top-0 h-auto lg:h-full">
                <div className="pt-2">
                    <h2 className="text-xl font-bold text-[#1a1f37] mb-1 tracking-tight">Asset Inventory</h2>
                    <p className="text-xs font-medium text-slate-500 leading-tight">Complete all sections to register your asset</p>
                </div>

                <div className="space-y-3">
                    <div className="flex justify-between items-end">
                        <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Overall Progress</span>
                        <span className="text-[11px] font-bold text-blue-600 uppercase">{progressPercent} %</span>
                    </div>
                    <div className="w-full bg-[#f0f9f3] h-1.5 rounded-full overflow-hidden">
                        <motion.div
                            className="bg-[#1a1f37] h-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                    <p className="text-[11px] font-medium text-slate-500 tracking-tight">{completedCount} of {WIZARD_STEPS.length} sections completed</p>
                </div>

                <nav className="flex flex-col gap-1 -mx-2">
                    {WIZARD_STEPS.map((step) => {
                        const isActive = currentStep === step.id;
                        const isCompleted = isStepValid(step.id);

                        return (
                            <button
                                key={step.id}
                                onClick={() => goToStep(step.id)}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all text-left relative group ${isActive
                                    ? 'bg-[#58CC022B]'
                                    : 'hover:bg-slate-50'
                                    }`}
                            >
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base transition-all ${isActive
                                    ? 'bg-[#1a1f37] text-white'
                                    : isCompleted
                                        ? 'bg-[#f0f9f3] text-green-600'
                                        : 'bg-[#f0f2f5] text-slate-400'
                                    }`}>
                                    {isCompleted && !isActive ? <FiCheck strokeWidth={3} /> : step.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-[14px] font-bold truncate tracking-tight ${isActive ? 'text-[#1a1f37]' : 'text-slate-500'}`}>
                                        {step.sidebarLabel}
                                    </p>
                                    <p className={`text-[11px] font-medium leading-none ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>
                                        {isActive ? 'In Progress' : isCompleted ? 'Completed' : 'Not Started'}
                                    </p>
                                </div>

                                {isActive && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1.5 h-8 bg-[#1a1f37] rounded-full" />
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* <div className="mt-auto pt-8 border-t border-gray-50">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        <p className="text-xs text-gray-400 font-medium italic">Auto-saving changes</p>
                    </div>
                </div> */}
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 flex flex-col h-full overflow-hidden bg-[#f8faff]">
                {/* Scrollable Content */}
                <div 
                    ref={scrollContainerRef}
                    className="flex-1 overflow-y-auto px-4 py-6 lg:px-8 lg:py-8 flex flex-col gap-4 custom-scrollbar"
                >
                    {/* Step Header Block - Compact & Premium */}
                    <div className="bg-[#010D2C] rounded-xl p-4 lg:p-5 text-white shadow-2xl shadow-blue-900/10 relative">
                        <div className="relative z-10 flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-[#58CC022B] flex items-center justify-center text-[10px] font-black text-[#58CC02]">
                                    {currentStep}
                                </div>
                                <span className="text-[10px] font-black text-[#58CC02] tracking-[0.2em]">
                                    Step {currentStep} of {WIZARD_STEPS.length}
                                </span>
                            </div>
                            <div>
                                <h1 className="text-xl lg:text-2xl font-black mb-1 lg:mb-1.5 tracking-tight">
                                    {activeStepData.title}
                                </h1>
                                <p className="text-gray-400 text-xs lg:text-sm font-medium max-w-2xl leading-relaxed">
                                    {activeStepData.description}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Step Form Content - Compact & Clean */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-gray-200/40 p-6 lg:p-8 animate-in fade-in slide-in-from-bottom-5 duration-500">
                        {children}
                    </div>
                </div>

                {/* Footer Navigation - Sticky */}
                <div className="flex items-center justify-between px-6 py-4 lg:px-10 bg-white border-t border-slate-100 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] z-50">
                    <button
                        onClick={prevStep}
                        className="px-10 py-3.5 rounded-xl border-2 border-slate-100 bg-white text-slate-900 text-sm font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-200 transition-all flex items-center gap-2 group"
                    >
                        <FiChevronLeft className="group-hover:-translate-x-1 transition-transform" />
                        Previous
                    </button>

                    <div className="flex items-center gap-6">
                        <button
                            onClick={saveDraft}
                            disabled={isSaving}
                            className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-900 transition-all"
                        >
                            {isSaving ? "Saving..." : "Save Draft"}
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={isSubmitting || (!canEarlyFinish && (currentStep === WIZARD_STEPS.length ? !isFormValid() : !isStepValid(currentStep)))}
                            className="px-12 py-3.5 rounded-xl bg-[#0b0f19] text-white text-sm font-black uppercase tracking-widest hover:bg-blue-950 transition-all flex items-center gap-2 shadow-xl shadow-blue-900/10 disabled:opacity-50 group min-w-[180px] justify-center"
                        >
                            {isSubmitting ? "Processing..." : canEarlyFinish ? "Save & Finish" : (currentStep === WIZARD_STEPS.length ? "Complete Setup" : "Continue")}
                            <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default WizardLayout;
